import { CBSocketClient, SocketClient } from "cbcore-ts"
import type { RHSpendCategory } from "../../../SocketClientFunctions"


export interface RHCPVHierarchy {
    cpv8: string
    level2: string
    level3: string
    level4: string
    level5: string
    level6: string
    level7: string
    level8: string
}

export interface RHCPVCodeRange {
    start: string
    end: string
}

// A CPV code as it arrives from forms and saved data: code text, a bare number, or nothing.
export type RHCPVValue = string | number | null | undefined


export class RHCPVCodeFormatter {
    static _autocompleteStrings?: string[]
    static _autocompleteStringsPromise?: Promise<string[]>
    static _labelsByCode?: Map<string, string>
    static readonly _socialAndSpecialServiceCPVCodeRanges: RHCPVCodeRange[] = [
        { start: "50116510", end: "50116510" },
        { start: "55100000", end: "55410000" },
        { start: "55510000", end: "55510000" },
        { start: "55511000", end: "55512000" },
        { start: "55520000", end: "55520000" },
        { start: "55521000", end: "55521200" },
        { start: "55522000", end: "55524000" },
        { start: "55523100", end: "55523100" },
        { start: "64000000", end: "64000000" },
        { start: "64100000", end: "64100000" },
        { start: "64110000", end: "64116000" },
        { start: "64122000", end: "64122000" },
        { start: "71550000", end: "71550000" },
        { start: "75000000", end: "75000000" },
        { start: "75100000", end: "75120000" },
        { start: "75121000", end: "75124000" },
        { start: "75125000", end: "75131000" },
        { start: "75200000", end: "75231000" },
        { start: "75231100", end: "75231240" },
        { start: "75240000", end: "75252000" },
        { start: "75300000", end: "75340000" },
        { start: "79100000", end: "79140000" },
        { start: "79430000", end: "79430000" },
        { start: "79611000", end: "79611000" },
        { start: "79622000", end: "79625000" },
        { start: "79700000", end: "79723000" },
        { start: "79950000", end: "79956000" },
        { start: "79995000", end: "79995200" },
        { start: "80000000", end: "80660000" },
        { start: "85000000", end: "85323000" },
        { start: "92000000", end: "92700000" },
        { start: "98000000", end: "98000000" },
        { start: "98113100", end: "98113100" },
        { start: "98120000", end: "98133110" },
        { start: "98200000", end: "98200000" },
        { start: "98500000", end: "98500000" },
        { start: "98513000", end: "98514000" },
        { start: "98900000", end: "98910000" }
    ]
    
    
    static codeFromCPVText(cpvValue: RHCPVValue): string {
        const cpvText = (cpvValue ?? "").toString().trim()
        if (!cpvText) {
            return ""
        }
        return cpvText.split(/\s+/)[0]?.trim() ?? ""
    }
    
    
    static async autocompleteStrings(): Promise<string[]> {
        if (this._autocompleteStrings) {
            return this._autocompleteStrings
        }
        
        if (!this._autocompleteStringsPromise) {
            this._autocompleteStringsPromise = SocketClient.RetrieveCPVCodes(
                undefined,
                CBSocketClient.completionPolicy.all
            ).then(response => {
                this._registerAutocompleteStrings(response.result || [])
                return this._autocompleteStrings ?? []
            }).catch(error => {
                this._autocompleteStringsPromise = undefined
                throw error
            })
        }
        
        return this._autocompleteStringsPromise
    }
    
    
    static displayTextForCPVUsingLoadedData(cpvValue: RHCPVValue): string {
        const cpvText = (cpvValue ?? "").toString().trim()
        if (!cpvText) {
            return ""
        }
        return this._displayTextForCPVWithLabels(cpvText, this._labelsByCode)
    }


    static descriptionForCPVUsingLoadedData(cpvValue: RHCPVValue): string {
        const displayText = this.labelForCPVUsingLoadedData(cpvValue)
        if (!displayText) {
            return ""
        }

        if (/^\s*\d{8}(?:-\d)?\s*$/.test(displayText)) {
            return ""
        }

        const descriptionMatch = displayText.match(
            /^\s*\d{8}(?:-\d)?(?:\s*[-\u2013\u2014:;|/]\s*|\s+)(.*?)\s*$/
        )
        if (!descriptionMatch) {
            return displayText
        }

        return descriptionMatch[1].trim()
    }
    
    
    static normalizedCPV8FromText(cpvValue: RHCPVValue): string {
        return this._normalizedCPV8FromCodeText(this.codeFromCPVText(cpvValue))
    }
    
    
    static displayCPV9FromText(cpvValue: RHCPVValue): string {
        const cpvCode = this.codeFromCPVText(cpvValue)
        if (!cpvCode) {
            return ""
        }
        
        if (/^\d{8}-\d$/.test(cpvCode)) {
            return cpvCode
        }
        
        const digits = this._digitsFromCPVText(cpvCode)
        if (digits.length >= 9) {
            return `${digits.substring(0, 8)}-${digits.substring(8, 9)}`
        }
        
        const cpv8 = this._normalizedCPV8FromCodeText(cpvCode)
        if (!cpv8) {
            return ""
        }
        return cpv8
    }
    
    
    static hierarchyForCPV(cpvValue: RHCPVValue): RHCPVHierarchy {
        const cpv8 = this.normalizedCPV8FromText(cpvValue)
        return {
            cpv8,
            level2: this._cpvLevelCode(cpv8, 2),
            level3: this._cpvLevelCode(cpv8, 3),
            level4: this._cpvLevelCode(cpv8, 4),
            level5: this._cpvLevelCode(cpv8, 5),
            level6: this._cpvLevelCode(cpv8, 6),
            level7: this._cpvLevelCode(cpv8, 7),
            level8: cpv8
        }
    }
    
    
    static _spendCategoryForCPV(cpvValue: RHCPVValue): RHSpendCategory | undefined {
        const cpv8 = this.normalizedCPV8FromText(cpvValue)
        if (!cpv8) {
            return undefined
        }
        
        if (this._isCPVCodeInRanges(cpv8, this._socialAndSpecialServiceCPVCodeRanges)) {
            return "SOTSIAAL_VOI_ERITEENUS"
        }
        
        if (cpv8.startsWith("45")) {
            return "EHITUSTOO"
        }
        
        const cpvNumber = Number(cpv8)
        if (Number.isFinite(cpvNumber) && cpvNumber >= 50000000) {
            return "TEENUS"
        }
        
        return "ASI"
    }
    
    
    static spendCategoryForCPVCodes(cpvValues: RHCPVValue[]): RHSpendCategory {
        const categories = new Set<RHSpendCategory>()
        for (const cpvValue of cpvValues) {
            const category = this._spendCategoryForCPV(cpvValue)
            if (category) {
                categories.add(category)
            }
        }
        
        if (categories.has("SOTSIAAL_VOI_ERITEENUS")) {
            return "SOTSIAAL_VOI_ERITEENUS"
        }
        if (categories.has("EHITUSTOO")) {
            return "EHITUSTOO"
        }
        if (categories.has("TEENUS")) {
            return "TEENUS"
        }
        return "ASI"
    }
    
    
    static labelForCPVUsingLoadedData(cpvValue: RHCPVValue): string {
        const cpvText = (cpvValue ?? "").toString().trim()
        if (!cpvText) {
            return ""
        }
        
        const directLabel = this.displayTextForCPVUsingLoadedData(cpvText)
        if (directLabel !== cpvText) {
            return directLabel
        }
        
        const cpv8 = this.normalizedCPV8FromText(cpvText)
        if (!cpv8 || !this._labelsByCode) {
            return cpvText
        }
        
        for (const label of this._labelsByCode.values()) {
            if (this.normalizedCPV8FromText(label) === cpv8) {
                return label
            }
        }
        
        return cpvText
    }
    
    
    static async displayTextForCPV(cpvValue: RHCPVValue): Promise<string> {
        const cpvText = (cpvValue ?? "").toString().trim()
        if (!cpvText) {
            return ""
        }
        
        await this.autocompleteStrings()
        return this._displayTextForCPVWithLabels(cpvText, this._labelsByCode)
    }
    
    
    static _displayTextForCPVWithLabels(
        cpvText: string,
        labelsByCode: Map<string, string> | undefined
    ): string {
        const cpvCode = this.codeFromCPVText(cpvText)
        if (!cpvCode || !labelsByCode) {
            return cpvText
        }
        
        return labelsByCode.get(cpvCode) ?? cpvText
    }
    
    
    static _registerAutocompleteStrings(strings: string[]) {
        this._autocompleteStrings = strings
        this._labelsByCode = new Map(
            strings.map(label => [this.codeFromCPVText(label), label] as const)
                .filter(([code]) => code.length > 0)
        )
    }
    
    
    static _normalizedCPV8FromCodeText(cpvText: string): string {
        const digits = this._digitsFromCPVText(cpvText)
        if (digits.length < 8) {
            return ""
        }
        return digits.substring(0, 8)
    }
    
    
    static _digitsFromCPVText(cpvText: string): string {
        return (cpvText ?? "").toString().replace(/\D/g, "")
    }
    
    
    static _cpvLevelCode(cpv8: string, level: number): string {
        if (cpv8.length !== 8) {
            return ""
        }
        return cpv8.substring(0, level).padEnd(8, "0")
    }
    
    
    static _isCPVCodeInRanges(cpv8: string, ranges: RHCPVCodeRange[]): boolean {
        const cpvNumber = Number(cpv8)
        return Number.isFinite(cpvNumber) && ranges.some(range => {
            const start = Number(range.start)
            const end = Number(range.end)
            return cpvNumber >= start && cpvNumber <= end
        })
    }
    
}
